import React, { useState, useCallback, useMemo, useRef } from 'react';
import { FigurePanel, PanelAnnotation, useFigureCompositionStore, FigureCompositionTheme } from '@/store/figure-composition';
import { AnnotationRenderer } from './AnnotationRenderer';

interface AnnotationLayerProps {
  panel: FigurePanel;
  panelWidth: number;
  panelHeight: number;
  theme: FigureCompositionTheme;
  zoom?: number; // Optional zoom factor (default 1)
}

export function AnnotationLayer({ panel, panelWidth, panelHeight, theme, zoom = 1 }: AnnotationLayerProps) {
  const selectedAnnotationId = useFigureCompositionStore((state) => state.selectedAnnotationId);
  const setSelectedAnnotationId = useFigureCompositionStore((state) => state.setSelectedAnnotationId);
  const setSelectedPanelId = useFigureCompositionStore((state) => state.setSelectedPanelId);
  const updateAnnotation = useFigureCompositionStore((state) => state.updateAnnotation);
  const [hoveredAnnotationId, setHoveredAnnotationId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number; annotationX: number; annotationY: number; annotationId: string } | null>(null);
  const [dragPosition, setDragPosition] = useState<{ x: number; y: number; x2?: number; y2?: number } | null>(null);
  const dragUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingUpdateRef = useRef<{ panelId: string; annotationId: string; updates: Partial<PanelAnnotation> } | null>(null);

  const annotations = panel.annotations ?? [];

  // Handle annotation click
  const handleAnnotationClick = useCallback(
    (e: React.MouseEvent, annotationId: string) => {
      e.stopPropagation(); // Prevent panel selection
      setSelectedAnnotationId(annotationId);
      setSelectedPanelId(panel.id); // Ensure panel is selected
      
      // Explicit focus management: move focus to properties panel header
      setTimeout(() => {
        const propertiesHeader = document.querySelector('[data-properties-panel-header]') as HTMLElement;
        if (propertiesHeader) {
          propertiesHeader.focus();
        }
      }, 0);
    },
    [panel.id, setSelectedAnnotationId, setSelectedPanelId]
  );

  // Handle mouse down for dragging
  const handleMouseDown = useCallback(
    (e: React.MouseEvent, annotation: PanelAnnotation) => {
      e.stopPropagation();
      setIsDragging(true);
      const svg = (e.currentTarget as SVGElement).closest('svg') as SVGSVGElement;
      if (!svg) return;

      const point = svg.createSVGPoint();
      point.x = e.clientX;
      point.y = e.clientY;
      const svgPoint = point.matrixTransform(svg.getScreenCTM()?.inverse());

      // Convert SVG coordinates to normalized (0-1)
      const normalizedX = svgPoint.x / panelWidth;
      const normalizedY = svgPoint.y / panelHeight;

      setDragStart({
        x: normalizedX,
        y: normalizedY,
        annotationX: annotation.x,
        annotationY: annotation.y,
        annotationId: annotation.id,
      });
      setDragPosition(null); // Reset drag position
      
      // Prevent text selection during drag
      e.preventDefault();
    },
    [panelWidth, panelHeight]
  );

  // Handle mouse move for dragging
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !dragStart) return;

      const svg = document.querySelector(`[data-panel-id="${panel.id}"] svg`) as SVGSVGElement;
      if (!svg) {
        // If SVG not found, cancel drag
        setIsDragging(false);
        setDragStart(null);
        setDragPosition(null);
        return;
      }

      try {
        const ctm = svg.getScreenCTM();
        if (!ctm) {
          setIsDragging(false);
          setDragStart(null);
          setDragPosition(null);
          return;
        }

        const point = svg.createSVGPoint();
        point.x = e.clientX;
        point.y = e.clientY;
        const svgPoint = point.matrixTransform(ctm.inverse());

        // Convert SVG coordinates to normalized (0-1)
        const normalizedX = svgPoint.x / panelWidth;
        const normalizedY = svgPoint.y / panelHeight;

        // Calculate delta from drag start
        const deltaX = normalizedX - dragStart.x;
        const deltaY = normalizedY - dragStart.y;

        const newX = Math.max(0, Math.min(1, dragStart.annotationX + deltaX));
        const newY = Math.max(0, Math.min(1, dragStart.annotationY + deltaY));

        // Find the annotation being dragged by ID (more reliable)
        const draggedAnnotation = annotations.find((a) => a.id === dragStart.annotationId);
        
        if (draggedAnnotation) {
          // Calculate new positions
          const updates: Partial<PanelAnnotation> = { x: newX, y: newY };
          
          // Also update x2/y2 if they exist (for arrows/brackets)
          if (draggedAnnotation.x2 !== undefined) {
            updates.x2 = Math.max(0, Math.min(1, (draggedAnnotation.x2 ?? 0) + deltaX));
          }
          if (draggedAnnotation.y2 !== undefined) {
            updates.y2 = Math.max(0, Math.min(1, (draggedAnnotation.y2 ?? 0) + deltaY));
          }
          
          // Immediate visual feedback - update drag position state
          setDragPosition({
            x: newX,
            y: newY,
            x2: updates.x2,
            y2: updates.y2,
          });
          
          // Throttle actual store updates (but keep visual feedback smooth)
          pendingUpdateRef.current = {
            panelId: panel.id,
            annotationId: draggedAnnotation.id,
            updates,
          };
          
          if (dragUpdateTimeoutRef.current) {
            clearTimeout(dragUpdateTimeoutRef.current);
          }
          
          // Reduced throttle for smoother feel (8ms = ~120fps for store updates)
          dragUpdateTimeoutRef.current = setTimeout(() => {
            if (pendingUpdateRef.current) {
              updateAnnotation(
                pendingUpdateRef.current.panelId,
                pendingUpdateRef.current.annotationId,
                pendingUpdateRef.current.updates
              );
              pendingUpdateRef.current = null;
            }
          }, 8); // Faster updates for smoother feel
        }
      } catch (error) {
        // If any error occurs during drag, cancel it
        console.warn('Error during annotation drag:', error);
        setIsDragging(false);
        setDragStart(null);
        setDragPosition(null);
      }
    },
    [isDragging, dragStart, panel.id, panelWidth, panelHeight, updateAnnotation, annotations]
  );

  // Handle mouse up to end dragging
  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      // Apply any pending update immediately
      if (dragUpdateTimeoutRef.current) {
        clearTimeout(dragUpdateTimeoutRef.current);
        dragUpdateTimeoutRef.current = null;
      }
      if (pendingUpdateRef.current) {
        updateAnnotation(
          pendingUpdateRef.current.panelId,
          pendingUpdateRef.current.annotationId,
          pendingUpdateRef.current.updates
        );
        pendingUpdateRef.current = null;
      }
      
      setIsDragging(false);
      setDragStart(null);
      setDragPosition(null); // Clear drag position
    }
  }, [isDragging, updateAnnotation]);

  // Set up global mouse event listeners for dragging
  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('mouseleave', handleMouseUp); // Handle mouse leaving window
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('mouseleave', handleMouseUp);
        // Clean up pending updates and timeouts
        if (dragUpdateTimeoutRef.current) {
          clearTimeout(dragUpdateTimeoutRef.current);
          dragUpdateTimeoutRef.current = null;
        }
        if (pendingUpdateRef.current) {
          // Apply final update before cleanup
          updateAnnotation(
            pendingUpdateRef.current.panelId,
            pendingUpdateRef.current.annotationId,
            pendingUpdateRef.current.updates
          );
          pendingUpdateRef.current = null;
        }
        setDragPosition(null);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp, updateAnnotation]);

  // Memoize annotation elements to avoid unnecessary re-renders
  const annotationElements = useMemo(
    () =>
      annotations.map((annotation) => {
        // Use drag position if this annotation is being dragged
        const isDraggingThis = isDragging && dragStart?.annotationId === annotation.id;
        const displayAnnotation = isDraggingThis && dragPosition
          ? { ...annotation, ...dragPosition }
          : annotation;
        
        return (
          <g
            key={annotation.id}
            onMouseDown={(e) => handleMouseDown(e, annotation)}
            style={{
              cursor: isDraggingThis ? 'grabbing' : 'grab',
            }}
          >
            <AnnotationRenderer
              annotation={displayAnnotation}
              panelWidth={panelWidth}
              panelHeight={panelHeight}
              theme={theme}
              isSelected={selectedAnnotationId === annotation.id}
              isHovered={hoveredAnnotationId === annotation.id}
              onMouseEnter={() => setHoveredAnnotationId(annotation.id)}
              onMouseLeave={() => setHoveredAnnotationId(null)}
              onClick={(e) => handleAnnotationClick(e, annotation.id)}
            />
          </g>
        );
      }),
    [annotations, panelWidth, panelHeight, theme, selectedAnnotationId, hoveredAnnotationId, handleAnnotationClick, handleMouseDown, isDragging, dragStart, dragPosition]
  );

  if (annotations.length === 0) {
    return null;
  }

  return (
    <svg
      data-panel-id={panel.id}
      className="absolute inset-0 pointer-events-none"
      style={{ 
        width: panelWidth, 
        height: panelHeight,
        cursor: isDragging ? 'grabbing' : 'default',
      }}
      viewBox={`0 0 ${panelWidth} ${panelHeight}`}
      aria-label={`Annotations for panel ${panel.label}`}
      role="group"
      aria-live="polite"
      aria-atomic="true"
    >
      {annotationElements}
    </svg>
  );
}

